import LocalizedStrings from 'react-localization';
import { withCommon } from '../../services/translation/commonTranslations';

const adminStrings = new LocalizedStrings({
  en: {
    menu: {
      general: 'General',
      locations: 'Locations',
      leaveTypes: 'Leave Types',
      skills: 'Skills',
      tasks: 'Tasks'
    },
    generalSection: {
      title: 'General Settings',
      defaultShiftTimes: 'Default Shift Times',
      defaultShiftTimesDescription:
        'This settings controls the default start and end of a shift when it is dragged onto a roster.',
      defaultShiftTimesDescriptionPart2: 'This can be overridden on a tenant or location level.',
      startTime: 'Start Time',
      endTime: 'End Time'
    },
    locationsSection: {
      title: 'Locations',
      table: {
        id: 'Id',
        name: 'Name',
        edit: 'Edit',
        addNew: 'Add New'
      },
      editLocationTimes: {
        defaultTimes: 'Default Times',
        title: (locationName: string) => `Default Times: ${locationName}`,
        startTime: 'Start Time',
        endTime: 'End Time',
        shiftTimes: {
          defaultShiftTimes: 'Default Shift Times',
          defaultShiftTimesDescription:
            'This setting controls the default start and end of a shift when it is dragged onto a roster.'
        },
        rosterTimes: {
          minTime: 'Minimum Time',
          maxTime: 'Maximum Time',
          defaultRosterTimes: 'Default Roster Times',
          defaultRosterTimesDescription:
            'This setting controls the default minimum and maximum times that shifts can be scheduled on a roster.',
          defaultRosterTimesDescriptionPart2: 'These values can be edited for individual rosters.',
          maxCannotBeLowerThanMin: 'Maximum time cannot be lower than the minimum time'
        },
        openingTimes: {
          defaultOpeningTimes: 'Default Opening Times',
          defaultOpeningTimesDescription:
            'This setting controls the default opening and closing times for the roster. Staff can still be rostered outside of these times, they are shown as a visual indicator only.',
          defaultOpeningTimesDescriptionPart2: 'These values can be edited for individual rosters.'
        }
      },
      editLocation: {
        locations: 'Locations',
        locationDetails: 'Location Details',
        title: (locationName: string) => `Location Details: ${locationName}`,
        name: 'Name',
        externalId: 'External ID',
        division: 'Division',
        holidayRegion: 'Holiday Region',
        locationName: 'Location Name',
        divisionRequired: 'Division is required',
        uniqueName: 'Location name must be unique.',
        uniqueId: 'Location external ID must be unique.',
        phoneNumber: 'Phone Number',
        address: 'Address',
        successMessage: (name: string) => `Location ${name} updated successfully.`,
        errorMessage: (name: string) => `Unable to update location ${name}.`
      },
      addDialog: {
        title: 'Add New Location',
        newLocation: 'New Location Name',
        locationName: 'Location Name',
        division: 'Division',
        divisionRequired: 'Division is required',
        holidayRegion: 'Holiday Region',
        description: 'Select the public holiday region used for this location.',
        inheritOption: 'Use tenant default',
        nameRequired: 'Location name required',
        uniqueName: 'Location name must be unique.',
        divisionPlaceholder: 'Select a division'
      }
    },
    skillsSection: {
      title: 'Skills',
      table: {
        id: 'Id',
        name: 'Name',
        edit: 'Edit',
        addNew: 'Add New'
      },
      editSkill: {
        skill: 'Skill',
        skillDetails: 'Skill Details',
        title: (skillName: string) => `Skill Details: ${skillName}`,
        name: 'Name',
        skillName: 'Skill Name',
        uniqueName: 'Skill name must be unique.',
        successMessage: (name: string) => `Skill ${name} updated successfully.`,
        errorMessage: (name: string) => `Unable to update skill ${name}.`
      },
      addDialog: {
        title: 'Add New Skill',
        newSkill: 'New Skill Name',
        skillName: 'Skill Name',
        nameRequired: 'Skill name required',
        uniqueName: 'Skill name must be unique.'
      }
    },
    tasksSection: {
      title: 'Tasks',
      table: {
        id: 'Id',
        name: 'Task Name',
        icon: 'Icon',
        skillsNeeded: 'Skills Needed',
        edit: 'Edit',
        addNew: 'Add New',
        disabled: 'DISABLED'
      },
      edit: {
        task: 'Task',
        taskDetails: 'Task Details',
        title: (taskName: string) => `Task Details: ${taskName}`,
        name: 'Name',
        taskName: 'Task Name',
        uniqueName: 'Task name must be unique.',
        successMessage: (name: string) => `Task ${name} updated successfully.`,
        errorMessage: (name: string) => `Unable to update task ${name}.`,
        textColour: 'Icon Colour',
        backgroundColour: 'Background Colour',
        taskIcon: 'Task Icon',
        requiredSkills: 'Required Skills',
        requiredSkillsDescription:
          'If this task is assigned to a shift, then only users with all of the checked skills can be assigned to that shift.'
      },
      addDialog: {
        title: 'Add New Task',
        newTask: 'New Task Name',
        taskName: 'Task Name',
        nameRequired: 'Task name required',
        uniqueName: 'Task name must be unique.'
      }
    },
    page: {
      admin: 'Admin',
      title: 'Admin Settings',
      locationTitle: 'Location Settings'
    },
    state: {
      advancedLeaveDisabled: 'Advanced leave is not enabled for this organisation.',
      loadingLeaveTypes: 'Loading leave types...'
    },
    leaveTypeTable: {
      name: 'Name',
      displayName: 'Display Name',
      color: 'Color',
      managerOnly: 'Manager Only',
      edit: 'Edit',
      delete: 'Delete',
      addNew: 'Add New',
      disabled: 'DISABLED'
    },
    deleteLeaveTypeDialog: {
      title: (name: string) => `Delete Leave Type: ${name}`,
      confirm: 'Continue',
      message: 'Are you sure you want to delete this leave type?',
      futureMessage:
        'This leave type is still associated to future leave requests, it cannot be removed until the requests are allocated to another leave type.',
      pastMessage:
        "There are historical leave requests using this leave type. The leave type will be replaced to 'unknown' in the historical record.",
      loading: 'Checking leave requests using this leave type...',
      validationError: 'Unable to validate leave type usage.',
      deleteSuccess: 'Leave type deleted.',
      deleteError: 'Unable to delete leave type.',
      futureListHeading: 'Future leave requests',
      unknownEmployee: 'Unknown employee'
    },
    editLeaveType: {
      createTitle: 'Add Leave Type',
      title: (name: string) => `Edit Leave Type: ${name}`,
      displayNameHelp: 'This will be displayed in the UI',
      code: 'Code',
      backgroundColor: 'Background Color',
      textColor: 'Text Colour',
      textColorBlack: 'Black',
      textColorWhite: 'White',
      managerOnly: 'Manager Only',
      managerOnlyDescription: 'Only managers can assign this leave type',
      activationTitle: 'Leave Type Status',
      deactivateDescription:
        'If you deactivate the leave type, users will not be able to create new leave requests of this type. Any existing requests will remain in the system.',
      activateDescription:
        'If you activate the leave type, users will be able to create new leave requests of this type again.',
      deactivate: 'Deactivate',
      activate: 'Activate',
      saveSuccess: 'Leave type saved.',
      saveError: 'Unable to save leave type.',
      createSuccess: 'Leave type created.',
      createError: 'Unable to create leave type.',
      discardTitle: 'Discard changes?',
      discardMessage: 'You have unsaved changes. If you cancel now, they will be lost.',
      discardConfirm: 'Discard',
      cancel: 'Cancel',
      save: 'Save'
    }
  }
});

export default withCommon<typeof adminStrings>(adminStrings);
